import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { buildQueryParams } from "../lib/globalUtils";

const EMAIL_CONFIG_QUERY_KEY = "email-config";

/**
 * SMTP configuration keys needed for email campaigns
 */
export const SMTP_CONFIG_KEYS = {
  HOST: "email_smtp_host",
  USERNAME: "email_smtp_username",
  PASSWORD: "email_smtp_password",
  ENCRYPTION: "email_smtp_encryption",
  PORT: "email_smtp_port",
  SENDER_EMAIL: "email_main",
  USE_DEFAULT: "email_smtp_use_default",
};

/**
 * Hook to get email/SMTP configuration from pro_white_labeling category
 */
export const useEmailConfig = () => {
  const queryParams = {
    where: [{ attribute: "key", value: "pro_white_labeling" }],
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: [EMAIL_CONFIG_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get(
        `/config_categories${queryString ? `?${queryString}` : ""}`,
      );
      const category = response?.result?.[0];

      if (!category?.configs) {
        return { configs: {}, raw: [], category: null };
      }

      const configMap = {};
      category.configs.forEach((config) => {
        configMap[config.key] = config;
      });

      return {
        configs: configMap,
        raw: category.configs,
        category,
      };
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

/**
 * Check if SMTP is configured (has required fields with values)
 * If using default SMTP (email_smtp_use_default = "1"), it's considered configured
 */
export const useIsSmtpConfigured = () => {
  const { data, isLoading, error } = useEmailConfig();

  const useDefault =
    data?.configs?.[SMTP_CONFIG_KEYS.USE_DEFAULT]?.value === "1";

  const hasCustomSmtp =
    !isLoading &&
    data?.configs &&
    data.configs[SMTP_CONFIG_KEYS.HOST]?.value &&
    data.configs[SMTP_CONFIG_KEYS.USERNAME]?.value &&
    data.configs[SMTP_CONFIG_KEYS.PASSWORD]?.value &&
    data.configs[SMTP_CONFIG_KEYS.SENDER_EMAIL]?.value;

  const isConfigured = useDefault || hasCustomSmtp;

  return {
    isConfigured,
    useDefault,
    isLoading,
    error,
    configs: data?.configs,
    raw: data?.raw,
  };
};

/**
 * Hook to update a single config value
 */
export const useUpdateEmailConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ configId, key, value }) => {
      const response = await apiClient.put(`/configs/${configId}`, {
        key,
        value,
      });
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMAIL_CONFIG_QUERY_KEY] });
    },
  });
};

/**
 * Hook to update multiple config values at once
 */
export const useSaveEmailConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (configUpdates) => {
      const promises = configUpdates.map(({ configId, key, value }) =>
        apiClient.put(`/configs/${configId}`, { key, value }),
      );

      const results = await Promise.all(promises);
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMAIL_CONFIG_QUERY_KEY] });
    },
  });
};

export default {
  useEmailConfig,
  useIsSmtpConfigured,
  useUpdateEmailConfig,
  useSaveEmailConfig,
  SMTP_CONFIG_KEYS,
};
